import { useState } from 'react';
import { useSelection } from '@/app/SelectionContext';
import { useKpis } from '@/app/hooks';
import { PageHeader } from '@/components/PageHeader';
import { Card } from '@/components/Card';
import { Dropdown } from '@/components/Dropdown';
import { EmptyState } from '@/components/EmptyState';
import { Spinner } from '@/components/LoadingScreen';
import {
  NO_DATA,
  formatAmd,
  formatChange,
  formatPercent,
  momChange,
  periodLabel,
  toNumber,
} from '@/utils/format';
import { font, v } from '@/theme/tokens';

const RESTAURANT_METRICS: [string, string, boolean, boolean][] = [
  // key, label, isPercent, increaseIsBad
  ['revenue', 'Выручка', false, false],
  ['cogs', 'Себестоимость', false, true],
  ['gross_profit', 'Валовая прибыль', false, false],
  ['payroll', 'ФОТ', false, true],
  ['total_expenses', 'Все расходы', false, true],
  ['operating_profit', 'Операционная прибыль', false, false],
  ['net_profit', 'Чистая прибыль', false, false],
  ['gross_margin', 'Валовая маржа', true, false],
  ['operating_margin', 'Операционная маржа', true, false],
  ['net_margin', 'Чистая маржа', true, false],
];

const KITCHEN_METRICS: [string, string, boolean, boolean][] = [
  ['total_cost', 'Полная стоимость', false, true],
  ['payroll', 'ФОТ', false, true],
  ['rent', 'Аренда', false, true],
  ['utilities', 'Коммунальные', false, true],
  ['production_expenses', 'Расходы производства', false, true],
  ['operational_expenses', 'Эксплуатация', false, true],
];

export function ComparePage() {
  const { selectedUnit, periods, selectedPeriod } = useSelection();
  const [aId, setAId] = useState<string | null>(null);
  const [bId, setBId] = useState<string | null>(null);

  const periodA = periods.find((p) => p.id === aId) ?? selectedPeriod ?? periods[0] ?? null;
  const indexA = periodA ? periods.findIndex((p) => p.id === periodA.id) : -1;
  const periodB = periods.find((p) => p.id === bId) ?? (indexA >= 0 ? periods[indexA + 1] : null) ?? null;

  const kpisA = useKpis(selectedUnit?.id, periodA?.id);
  const kpisB = useKpis(selectedUnit?.id, periodB?.id);

  const isKitchen = selectedUnit?.type === 'PRODUCTION_KITCHEN';
  const metrics = isKitchen ? KITCHEN_METRICS : RESTAURANT_METRICS;

  const options = periods.map((p) => ({ value: p.id, label: periodLabel(p.year, p.month) }));

  return (
    <div style={{ animation: 'fade-in 0.35s ease' }}>
      <PageHeader
        title="Сравнение периодов"
        subtitle={selectedUnit ? `${selectedUnit.name} · период к периоду` : 'Сравнение'}
      />

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 18, flexWrap: 'wrap' }}>
        <Dropdown icon="calendar" value={periodA?.id ?? null} label={periodA ? periodLabel(periodA.year, periodA.month) : 'Период A'} options={options} onSelect={setAId} />
        <span style={{ color: v('ink-muted'), fontWeight: 600, fontSize: 13 }}>против</span>
        <Dropdown icon="calendar" value={periodB?.id ?? null} label={periodB ? periodLabel(periodB.year, periodB.month) : 'Период B'} options={options} onSelect={setBId} />
      </div>

      {(!periodA || !periodB) && (
        <EmptyState title="Недостаточно периодов" description="Для сравнения нужно минимум два финансовых периода с данными." icon="calendar" />
      )}

      {periodA && periodB && (kpisA.isPending || kpisB.isPending) && <Spinner />}

      {periodA && periodB && kpisA.data && kpisB.data && (
        !kpisA.data.has_data && !kpisB.data.has_data ? (
          <EmptyState title="Нет данных" description="Ни один из выбранных периодов не содержит подтверждённых данных." icon="inbox" />
        ) : (
          <Card>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
              <thead>
                <tr>
                  {['Показатель', periodLabel(periodA.year, periodA.month), periodLabel(periodB.year, periodB.month), 'Δ', 'Δ %'].map((header, index) => (
                    <th key={header} style={{ textAlign: index === 0 ? 'left' : 'right', padding: '10px 12px', fontSize: 12, color: v('ink-muted'), borderBottom: `2px solid ${v('line')}` }}>
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {metrics.map(([key, label, isPercent, increaseIsBad]) => {
                  const a = toNumber(kpisA.data.kpis[key]?.value);
                  const b = toNumber(kpisB.data.kpis[key]?.value);
                  const diff = a != null && b != null ? a - b : null;
                  const pct = isPercent ? (a != null && b != null ? a - b : null) : momChange(a, b);
                  const good = diff == null ? null : increaseIsBad ? diff < 0 : diff > 0;
                  return (
                    <tr key={key}>
                      <td style={{ padding: '10px 12px', borderBottom: `1px solid ${v('line-soft')}`, fontWeight: 600 }}>{label}</td>
                      <td style={{ padding: '10px 12px', borderBottom: `1px solid ${v('line-soft')}`, textAlign: 'right', fontFamily: font.mono }}>
                        {isPercent ? formatPercent(a) : formatAmd(a)}
                      </td>
                      <td style={{ padding: '10px 12px', borderBottom: `1px solid ${v('line-soft')}`, textAlign: 'right', fontFamily: font.mono, color: v('ink-sub') }}>
                        {isPercent ? formatPercent(b) : formatAmd(b)}
                      </td>
                      <td style={{ padding: '10px 12px', borderBottom: `1px solid ${v('line-soft')}`, textAlign: 'right', fontFamily: font.mono, fontWeight: 600, color: good == null ? v('ink-muted') : good ? v('positive') : v('negative') }}>
                        {diff == null ? NO_DATA : isPercent ? formatChange(diff) : (diff >= 0 ? '+' : '−') + formatAmd(Math.abs(diff)).replace('−', '')}
                      </td>
                      <td style={{ padding: '10px 12px', borderBottom: `1px solid ${v('line-soft')}`, textAlign: 'right', fontFamily: font.mono, color: v('ink-sub') }}>
                        {isPercent ? (pct == null ? NO_DATA : formatChange(pct) + ' п.п.'.replace('%', '')) : pct == null ? NO_DATA : formatChange(pct)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{ fontSize: 12, color: v('ink-muted'), marginTop: 10 }}>
              Сравнение объектов между собой станет доступно, когда появится второй ресторан с данными.
            </div>
          </Card>
        )
      )}
    </div>
  );
}
